/**
 * AudioUploadDialog — upload an audio file → transcribe → save the
 * transcript as a Lecture. The audio itself is **never persisted** server-
 * side; the backend `/api/transcribe` endpoint processes the bytes through
 * a temp file that is deleted as soon as Whisper returns. Only the
 * resulting transcript is written to MongoDB via POST `/lectures`.
 *
 * Why this design
 * ---------------
 * Storing raw audio in R2 is expensive (lectures are 30–60 MB each, 10k
 * users × 5 lectures/month = ~1 TB/month). The transcript is what students
 * actually study from, so we keep the cheap thing and drop the expensive
 * thing. This component exists to make that flow obvious in the UI.
 *
 * Wired into LecturesPage as a third button next to "New Lecture" / "Import".
 */
import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mic, FileAudio, Loader2, Sparkles, X, Save } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

const API = process.env.REACT_APP_BACKEND_URL;
const ACCEPT = '.mp3,.m4a,.wav,.webm,.ogg,.mp4,.mpga,.mpeg';
const MAX_BYTES = 100 * 1024 * 1024; // 100 MB safety ceiling

// Stage state machine — kept as a single string so the JSX reads top-to-
// bottom and there's only ever one branch active.
const STAGE = {
  PICK: 'pick',          // waiting for the user to choose a file
  TRANSCRIBING: 'tx',    // POSTing to /api/transcribe
  REVIEW: 'review',      // transcript returned, ready to edit + save
};

const AudioUploadDialog = ({ subjects = [], onLectureCreated, triggerClassName = '' }) => {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState(STAGE.PICK);
  const [fileName, setFileName] = useState('');
  const [transcript, setTranscript] = useState('');
  const [title, setTitle] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // Reset everything on close so a fresh open never inherits stale state.
  const reset = () => {
    setStage(STAGE.PICK);
    setFileName('');
    setTranscript('');
    setTitle('');
    setSubjectId('');
    setSaving(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = (next) => {
    setOpen(next);
    if (!next) reset();
  };

  const handlePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error('Audio file is too large. Max 100 MB.');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setFileName(file.name);
    setStage(STAGE.TRANSCRIBING);
    setTitle(file.name.replace(/\.[^.]+$/, '')); // sensible default title

    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/transcribe`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Long timeout — a 60-min lecture takes ~30-60s to transcribe
        timeout: 5 * 60 * 1000,
      });
      const text = (res.data?.transcript || '').trim();
      if (!text) {
        toast.error("Couldn't extract any speech from this file. Try a different recording.");
        setStage(STAGE.PICK);
        return;
      }
      setTranscript(text);
      setStage(STAGE.REVIEW);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("You're transcribing a lot right now. Wait a minute and try again.");
      } else {
        toast.error(err?.response?.data?.detail || 'Transcription failed. Please try again.');
      }
      setStage(STAGE.PICK);
    }
  };

  const handleSave = async () => {
    if (!subjectId) { toast.error('Pick a subject to save into.'); return; }
    if (!title.trim()) { toast.error('Give your lecture a title.'); return; }
    if (!transcript.trim()) { toast.error('Transcript is empty — nothing to save.'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/lectures`, {
        subject_id: subjectId,
        title: title.trim(),
        transcript: transcript.trim(),
      });
      toast.success('Lecture saved. Audio file discarded as promised.');
      if (onLectureCreated) onLectureCreated(data);
      handleClose(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className={`gap-2 rounded-xl border-[var(--isp-glass-border)] text-[var(--isp-heading)] hover:bg-[#002FA7]/5 ${triggerClassName}`}
          data-testid="upload-audio-button"
        >
          <FileAudio className="w-4 h-4" />
          Upload Audio
        </Button>
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-2xl bg-[var(--isp-glass)] border-[var(--isp-glass-border)] text-[var(--isp-heading)] max-h-[90vh] overflow-y-auto"
        data-testid="audio-upload-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <FileAudio className="w-6 h-6 text-[#002FA7]" />
            Upload Audio for Transcription
          </DialogTitle>
          <p className="text-xs text-[var(--isp-muted)] mt-1.5 leading-relaxed">
            We&apos;ll transcribe your file with AI and save the transcript to your subject. The audio file itself is <span className="font-semibold text-[var(--isp-heading)]">never stored</span> — only the text is kept.
          </p>
        </DialogHeader>

        {/* Stage 1 — file picker */}
        {stage === STAGE.PICK && (
          <div className="mt-5 space-y-4">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full border-2 border-dashed border-[var(--isp-glass-border)] hover:border-[#002FA7] hover:bg-[#002FA7]/5 rounded-2xl p-10 flex flex-col items-center gap-3 transition-colors"
              data-testid="audio-upload-picker"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#002FA7]/10 flex items-center justify-center">
                <FileAudio className="w-7 h-7 text-[#002FA7]" />
              </div>
              <div className="text-center">
                <p className="text-base font-semibold text-[var(--isp-heading)]">Tap to choose an audio file</p>
                <p className="text-xs text-[var(--isp-muted)] mt-1">.mp3 · .m4a · .wav · .webm · .ogg — up to 100 MB</p>
              </div>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              onChange={handlePick}
              className="hidden"
              data-testid="audio-upload-input"
            />
            <div className="flex items-start gap-2 p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
              <Sparkles className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <p className="text-xs text-[var(--isp-body)] leading-relaxed">
                Already have a recording from class? Drop it here — you&apos;ll have a searchable transcript in under a minute.
              </p>
            </div>
          </div>
        )}

        {/* Stage 2 — transcribing */}
        {stage === STAGE.TRANSCRIBING && (
          <div className="mt-5 py-12 flex flex-col items-center gap-4" data-testid="audio-transcribing">
            <Loader2 className="w-10 h-10 text-[#002FA7] animate-spin" />
            <div className="text-center">
              <p className="text-base font-semibold text-[var(--isp-heading)]">Transcribing {fileName}…</p>
              <p className="text-xs text-[var(--isp-muted)] mt-1">This usually takes 30–60 seconds for a one-hour lecture.</p>
            </div>
          </div>
        )}

        {/* Stage 3 — review + save */}
        {stage === STAGE.REVIEW && (
          <div className="mt-5 space-y-4" data-testid="audio-review-stage">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="audio-lecture-title" className="text-xs font-semibold uppercase tracking-wider text-[var(--isp-muted)]">Title</Label>
                <Input
                  id="audio-lecture-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Mitosis Lecture 1"
                  className="bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)]"
                  data-testid="audio-lecture-title"
                  maxLength={120}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-[var(--isp-muted)]">Save into subject</Label>
                <Select value={subjectId} onValueChange={setSubjectId}>
                  <SelectTrigger className="bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)]" data-testid="audio-subject-select">
                    <SelectValue placeholder="Pick a subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjects.length === 0 && (
                      <SelectItem value="__none" disabled>Create a subject first</SelectItem>
                    )}
                    {subjects.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="audio-transcript" className="text-xs font-semibold uppercase tracking-wider text-[var(--isp-muted)]">Transcript ({transcript.split(/\s+/).filter(Boolean).length} words)</Label>
                <span className="text-[10px] text-[var(--isp-muted)]">Editable — tidy it before saving</span>
              </div>
              <Textarea
                id="audio-transcript"
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                rows={10}
                className="bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] font-mono text-sm leading-relaxed"
                data-testid="audio-transcript-textarea"
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
              <Button
                variant="ghost"
                onClick={() => handleClose(false)}
                className="text-[var(--isp-muted)] hover:text-[var(--isp-heading)]"
                data-testid="audio-cancel-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !subjectId || !title.trim()}
                className="isp-btn-primary gap-2"
                data-testid="audio-save-lecture-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Saving transcript…' : 'Save as Lecture'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AudioUploadDialog;
